// The chat window: the list on one side, the open conversation on the other.
// Both listeners behind it live in FirebaseContextProvider — the badge needs
// them whether or not this window is open.

import { useState, useEffect, useRef } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import {
  assistantChatId,
  isAssistantChat,
  chatTitle,
  otherMemberId,
  otherMemberIds,
} from '../utils/chat';
import { useEntities } from '../utils/hooks';
import { chatListTime } from '../utils/misc';
import { useFirebase } from './FirebaseContextProvider';
import Avatar from './Avatar';
import ChatThread from './ChatThread';
import './ChatsModalBody.scss';

export default function ChatsModalBody({ initialChatId }) {
  const { user, chats, isChatsLoaded, isChatsSynced, usersById } =
    useFirebase();
  const uid = user?.id;

  // Nothing subscribes to `users` as a collection, so the people behind these
  // chats are named and read by id — without this `usersById` never holds them
  // and every row falls back to the name frozen into the last message
  useEntities('users', otherMemberIds(chats, uid));

  const [openId, setOpenId] = useState(initialChatId ?? null);
  // Pinned once: opening a chat marks it read, and a rule recomputed after
  // that would send the window straight back to the list
  const pickedRef = useRef(!!initialChatId);

  const openChat = chats.find((chat) => chat.id === openId) ?? null;

  // The one chat that opens on its own, and only while it has something
  // unread — every other case starts on the list
  useEffect(() => {
    if (pickedRef.current || !isChatsSynced) return;
    pickedRef.current = true;

    const assistant =
      chats.find((chat) => chat.id === assistantChatId(uid)) ??
      chats.find(isAssistantChat);
    if (assistant?.hasUnread) setOpenId(assistant.id);
  }, [isChatsSynced, chats, uid]);

  // A push can name a chat that is not in the list any more. Falling back to
  // the list beats a pane that stays blank.
  useEffect(() => {
    if (openId && isChatsSynced && !openChat) setOpenId(null);
  }, [openId, isChatsSynced, openChat]);

  const renderRow = (chat) => {
    const otherId = otherMemberId(chat, uid);
    const preview = chat.lastMessage?.text ?? chat.lastMessage?.title ?? '';

    return (
      <li
        className={'chat-row' + (chat.id === openId ? ' active' : '')}
        key={chat.id}
        onClick={() => setOpenId(chat.id)}
      >
        {isAssistantChat(chat) ? (
          <div className='assistant-avatar wh-32'>M</div>
        ) : (
          <Avatar user={usersById.get(otherId)} size={32} />
        )}

        <div className='row-text'>
          <h6 className='name'>{chatTitle(chat, uid, usersById)}</h6>
          <p className='preview font-12'>{preview}</p>
        </div>

        <div className='row-side'>
          <p className='time font-12'>{chatListTime(chat.lastMessageAt)}</p>
          {chat.hasUnread && <span className='unread-dot' />}
        </div>
      </li>
    );
  };

  return (
    <div className={'chats-modal' + (openChat ? ' thread-open' : '')}>
      <div className='chats-list'>
        {!chats.length && !isChatsLoaded && (
          <div className='list-loading'>
            <ClipLoader size={24} color='#3f90dc' />
          </div>
        )}
        {/* A conclusion from absence, so it waits for the server */}
        {!chats.length && isChatsSynced && (
          <p className='empty font-16'>No chats yet</p>
        )}
        <ul className='rows'>{chats.map(renderRow)}</ul>
      </div>

      <div className='chats-thread'>
        {openChat ? (
          <ChatThread
            key={openChat.id}
            chat={openChat}
            onBack={() => setOpenId(null)}
          />
        ) : (
          <p className='empty font-16'>Pick a chat to open it</p>
        )}
      </div>
    </div>
  );
}
